import { fetchLyrics, toggleLikeSong } from '../harness/justlyrics-harness';
import { JUSTLYRICS_PROMPTS } from '../prompts/justlyrics';

export async function buildJustLyricsContext(textPrompt: string): Promise<string> {
    const lowerPrompt = textPrompt.toLowerCase();
    
    // Intent Detection
    const isAskingForPlaylist = lowerPrompt.includes("playlist") || lowerPrompt.includes("mix") || lowerPrompt.includes("queue") || lowerPrompt.includes("next song");
    const isAskingToLike = lowerPrompt.includes("like this song") || lowerPrompt.includes("add this to my likes") || lowerPrompt.includes("save this song");
    const isAskingToUnlike = lowerPrompt.includes("unlike this song") || lowerPrompt.includes("remove from my likes");
    const isAskingForLyrics = lowerPrompt.includes("lyrics to") || lowerPrompt.includes("lyrics for");
    const isAskingForMeaning = lowerPrompt.includes("meaning of") || lowerPrompt.includes("what is the song") || lowerPrompt.includes("explain the song");
    const isAskingToPlay = lowerPrompt.includes("play ") || lowerPrompt.includes("listen to ");

    if (isAskingToLike || isAskingToUnlike) {
        let currentTrack = "";
        try {
            const iframes = document.querySelectorAll('iframe[src*="justlyrics.kaywat.codes"]');
            if (iframes.length > 0) {
                const lastIframe = iframes[iframes.length - 1] as HTMLIFrameElement;
                const url = new URL(lastIframe.src);
                currentTrack = url.searchParams.get('track') || "";
                if (!currentTrack) {
                    const tracksParam = url.searchParams.get('tracks');
                    if (tracksParam) {
                        currentTrack = tracksParam.split(',')[0].trim();
                    }
                }
            }
        } catch (e) {}

        if (currentTrack) {
            const success = await toggleLikeSong(currentTrack, isAskingToLike);
            if (success) {
                if (typeof (window as any).renderJustLyricsLibrary === 'function') {
                    (window as any).renderJustLyricsLibrary();
                }
                return isAskingToLike ? JUSTLYRICS_PROMPTS.SONG_LIKED(currentTrack) : JUSTLYRICS_PROMPTS.SONG_UNLIKED(currentTrack);
            }
        }
        return `[System: JustLyrics Bridge Failed] Tell the user you couldn't identify the currently playing song or there was a database error.`;
    }

    if (isAskingForPlaylist) {
        let currentTrackContext = "";
        try {
            // Check the DOM for the most recent JustLyrics iframe
            const iframes = document.querySelectorAll('iframe[src*="justlyrics.kaywat.codes"]');
            if (iframes.length > 0) {
                const lastIframe = iframes[iframes.length - 1] as HTMLIFrameElement;
                const url = new URL(lastIframe.src);
                let currentTrack = url.searchParams.get('track');
                let currentArtist = url.searchParams.get('artist') || "";
                
                // If it's a playlist iframe, get the first track
                if (!currentTrack) {
                    const tracksParam = url.searchParams.get('tracks');
                    if (tracksParam) {
                        const firstTrackStr = tracksParam.split(',')[0].trim();
                        currentTrack = firstTrackStr;
                    }
                }
                
                if (currentTrack) {
                    currentTrackContext = `\nIMPORTANT CRITICAL INSTRUCTION: The user is CURRENTLY listening to "${currentTrack}"${currentArtist ? ' by ' + currentArtist : ''}. You MUST make this exact song the VERY FIRST track in the playlist queue so playback is uninterrupted, and then curate the rest of the mix after it.`;
                }
            }
        } catch (e) {
            console.warn("Could not extract active track", e);
        }
        
        return JUSTLYRICS_PROMPTS.PLAYLIST_MODE(textPrompt) + currentTrackContext;
    }

    if (isAskingForLyrics || isAskingForMeaning || isAskingToPlay) {
        // Basic extraction (e.g. "play [song]", "lyrics to [song]")
        const match = lowerPrompt.match(/(?:lyrics to|lyrics for|meaning of|explain the song|play|listen to) (.+)/i);
        const trackQuery = match ? match[1].replace(/by.*/, "").trim() : "";
        
        if (trackQuery) {
            const data = await fetchLyrics(trackQuery);
            if (data) {
                if (isAskingToPlay) {
                    return JUSTLYRICS_PROMPTS.PLAY_MODE(data.track, data.artist);
                }
                if (isAskingForMeaning) {
                    return JUSTLYRICS_PROMPTS.ANALYSIS_MODE(data.track, data.artist, data.lyrics);
                }
                return JUSTLYRICS_PROMPTS.LYRICS_FOUND(data.track, data.artist, data.lyrics);
            } else {
                // If it's a play request but not in DB, fallback to generic Play Mode prompt
                if (isAskingToPlay) {
                    let finalTrack = trackQuery;
                    let finalArtist = "";
                    if (trackQuery.includes(" by ")) {
                        const parts = trackQuery.split(" by ");
                        finalTrack = parts[0].trim();
                        finalArtist = parts[1].trim();
                    }
                    return JUSTLYRICS_PROMPTS.PLAY_MODE(finalTrack, finalArtist);
                }
                return JUSTLYRICS_PROMPTS.LYRICS_FAILED();
            }
        }
    }
    
    return "";
}
